import React, { useState, useRef, useEffect, useMemo } from 'react';
import { PRESET_CITIES } from '../utils/usStates';
import './CityTypeahead.css';

export default function CityTypeahead({ value, onChange, onSave, onCancel, existingCities = [], placeholder = 'City' }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const ref = useRef(null);
  const inputRef = useRef(null);

  // Combine preset + user's existing custom cities, deduplicated
  const allCities = useMemo(() => {
    return [...new Set([...PRESET_CITIES, ...existingCities])].sort();
  }, [existingCities]);

  // Filter suggestions as user types
  const suggestions = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase().trim();
    return allCities.filter(c => c.toLowerCase().includes(q)).slice(0, 8);
  }, [query, allCities]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function handleSelect(city) {
    setQuery(city);
    onChange(city);
    setOpen(false);
    setHighlightIdx(-1);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      if (highlightIdx >= 0 && highlightIdx < suggestions.length) {
        handleSelect(suggestions[highlightIdx]);
      } else {
        onChange(query);
        onSave();
      }
      return;
    }
    if (e.key === 'Escape') { onCancel(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, suggestions.length - 1));
      setOpen(true);
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, -1));
    }
  }

  return (
    <div className="city-typeahead" ref={ref}>
      <input
        ref={inputRef}
        className="role-location-city-input"
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); setHighlightIdx(-1); }}
        onFocus={() => { if (query.trim() && suggestions.length > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={40}
      />
      {open && suggestions.length > 0 && (
        <div className="city-typeahead-dropdown">
          {suggestions.map((city, i) => (
            <div
              key={city + i}
              className={`city-typeahead-option ${i === highlightIdx ? 'highlighted' : ''}`}
              onMouseDown={e => { e.preventDefault(); handleSelect(city); }}
              onMouseEnter={() => setHighlightIdx(i)}
            >
              {city}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
